"use client";
import { createContext, useContext, useMemo } from "react";
import { Search, SearchResult } from "../search";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useSearchParams } from "next/navigation";
import { getFilterFromSearchParams } from "@/search/search-filter-selectors";
import { useFilterFacet } from "../hooks/useFilterFacet";

type ItemListingContextValue = SearchResult & {
  search: Search;
} & ReturnType<typeof useInfiniteScroll> &
  ReturnType<typeof useFilterFacet>;

const ItemListingContext = createContext<ItemListingContextValue>({
  isLastPage: false,
  facets: {},
  isLoading: false,
  loadMore: async () => {},
  filterFacet: async () => {},
  nbHits: 0,
  results: [],
  searchId: "",
  search: {},
  filteredFacets: {},
});

export function ItemListingProvider({
  children,
  initialResult,
  search: initialSearch,
}: {
  children: React.ReactNode;
  search: Search;
  initialResult: SearchResult;
}) {
  const searchParams = useSearchParams();
  const search = useMemo(() => {
    const facetFilter = getFilterFromSearchParams(
      searchParams,
      initialSearch.facets,
    );

    return {
      ...initialSearch,
      filters: [...(initialSearch.filters ?? []), ...facetFilter],
      query: searchParams.get("query") ?? "",
    };
  }, [initialSearch, searchParams]);

  const results = useInfiniteScroll({
    search,
    initialResult,
  });

  const filterFacet = useFilterFacet({ search });

  return (
    <ItemListingContext.Provider value={{ search, ...results, ...filterFacet }}>
      {children}
    </ItemListingContext.Provider>
  );
}

export function useItemListing() {
  return useContext(ItemListingContext);
}
